import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ServiceError, useError } from 'Src/network/useError';

import service, { OloConfig } from './oloConfigService';

const queryKeys = {
  get: () => ['POS:GET:OloConfig'],
};

const useQuery_OloConfigGet = <TData = OloConfig>(select?: (data: OloConfig) => TData) => {
  const { t } = useTranslation();
  const { parseError } = useError({ activity: t('olo.errors.getOloConfig') });
  return useQuery<OloConfig, ServiceError, TData>({
    queryKey: queryKeys.get(),
    queryFn: async () => {
      try {
        return await service.getConfig({});
      } catch (e) {
        throw parseError(e);
      }
    },
    staleTime: 0,
    select,
  });
};

const useInvalidate_OloConfigGet = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.get() });
};

const useMutation_UpdateThrottlingInfo = () => {
  const { t } = useTranslation();
  const { parseError } = useError({ activity: t('olo.errors.updateThrottlingInfo') });
  return useMutation({
    mutationFn: async (...args: Parameters<typeof service.updateThrottlingInfo>) => {
      try {
        return await service.updateThrottlingInfo(...args);
      } catch (e) {
        throw parseError(e);
      }
    },
  });
};

const useQuery_GetLeadTimes = () => {
  const select = useCallback((data: OloConfig) => {
    const takeoutLeadTime = data.takeoutLeadTimeMinOverride > 0 ? data.takeoutLeadTimeMinOverride : data.takeoutLeadTimeMin;
    const deliveryLeadTime = data.deliveryLeadTimeMinOverride > 0 ? data.deliveryLeadTimeMinOverride : data.deliveryLeadTimeMin;
    return { takeoutLeadTime, deliveryLeadTime };
  }, []);
  return useQuery_OloConfigGet(select);
};

const useOloConfig = {
  query: {
    get: useQuery_OloConfigGet,
    getLeadTimes: useQuery_GetLeadTimes,
  },
  invalidate: {
    get: useInvalidate_OloConfigGet,
  },
  mutate: {
    updateThrottlingInfo: useMutation_UpdateThrottlingInfo,
  },
};

export { useOloConfig };
